import { Dimensions, PixelRatio } from 'react-native';

// Base width for scaling calculations (iPhone 13/14 standard width)
const BASE_WIDTH = 390;

// Minimum and maximum scale factors to prevent text from being too small or too large
const MIN_SCALE = 0.85;
const MAX_SCALE = 1.15;

/**
 * Gets the current screen width
 */
export const getScreenWidth = (): number => {
  return Dimensions.get('window').width;
};

/**
 * Calculates a font scale multiplier based on screen width
 * @returns Scale multiplier between MIN_SCALE and MAX_SCALE
 */
export const getFontScaleMultiplier = (): number => {
  const screenWidth = getScreenWidth();

  // Calculate scale based on screen width relative to base width
  let scale = screenWidth / BASE_WIDTH;

  // Clamp scale between min and max values
  scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));

  return scale;
};

/**
 * Scales a font size responsively based on screen dimensions
 * @param size - Base font size in pixels
 * @param respectAccessibility - Whether to respect device font scale settings (default: true)
 * @returns Scaled font size
 */
export const scaleFont = (size: number, respectAccessibility: boolean = true): number => {
  const scaleMultiplier = getFontScaleMultiplier();
  const scaledSize = size * scaleMultiplier;

  if (respectAccessibility) {
    // Respect user's device font scale preferences for accessibility
    return Math.round(PixelRatio.roundToNearestPixel(scaledSize));
  }

  return Math.round(scaledSize);
};

/**
 * Scales line height proportionally to font size
 * @param baseSize - Base font size in pixels
 * @param baseLineHeight - Base line height in pixels
 * @returns Scaled line height
 */
export const scaleLineHeight = (baseSize: number, baseLineHeight: number): number => {
  const ratio = baseLineHeight / baseSize;
  const scaledSize = scaleFont(baseSize);
  return Math.round(scaledSize * ratio);
};

/**
 * Responsive typography scale for quiz components
 */
export const quizTypography = {
  title: {
    fontSize: scaleFont(20),
    lineHeight: scaleLineHeight(20, 28),
  },
  text: {
    fontSize: scaleFont(14),
    lineHeight: scaleLineHeight(14, 25),
  },
  smallText: {
    fontSize: scaleFont(10),
    lineHeight: scaleLineHeight(10, 18),
  },
  reason: {
    fontSize: scaleFont(11),
    lineHeight: scaleLineHeight(11, 18),
  },
};

/**
 * Gets screen size category for conditional styling
 */
export const getScreenSizeCategory = (): 'small' | 'medium' | 'large' => {
  const width = getScreenWidth();

  if (width < 350) {
    return 'small';
  } else if (width < 400) {
    return 'medium';
  }
  return 'large';
};
